import styled from '@emotion/styled'
import { throttle, uniq } from 'lodash'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { OnCallType, Profession, ShiftActivity } from '@bemlo/enums'
import { EMPTY_FROZEN_ARRAY, Enumerable, Falsy, isTruthy } from '@bemlo/utils'

import { COLUMN_WIDTH } from './consts'
import {
  AvailableShiftChannels,
  ScheduleZoom,
  UIAvailability,
  UICandidate,
  UIDraftShift,
  UIShift,
} from './types'
import { getScheduleShiftStatus } from './useUIShiftsFromSlots'
import { ScheduleDay } from './utils/get-days-in-range'
import { convertUIDraftShiftToShift } from './utils/ui-draft-shift-to-shift'

const Container = styled.div<{ isHorizontallyScrollable: boolean }>(
  ({ isHorizontallyScrollable }) => ({
    overflowX: isHorizontallyScrollable ? 'auto' : 'hidden',
    position: 'relative',
    ...(isHorizontallyScrollable && {
      width: 0,
      flex: 1,
    }),
  }),
)

export type ShiftPopover = FC<{ shiftId: Enumerable<string> }>

export type AvailabilityPopover = FC<{
  candidate: { id: string }
  availability: UIAvailability[]
}>

export type DraftShiftInput = {
  activity: ShiftActivity
  date: string
  startTime: string
  endTime: string
  comment: string
  tags: { id: string }[]
  breakTime: number
  onCallTime:
    | {
        type: OnCallType
        startTime: string
        endTime: string
      }
    | null
    | undefined
  scheduleResourceId: string
  scheduleSlotId: string | undefined
  bookedCandidateId: string | undefined
  rowKey: number | undefined
}

type Resource = {
  rowKey: number | undefined
  id: string
  fullName: string
  profession: Profession
  homeUnitId: string | undefined
}

export type EnableEditing = {
  draftShifts: UIDraftShift[]
  onCreate: (data: DraftShiftInput) => void
  onDelete: (id: string) => void
}

export type EnableSelectShifts = {
  selectedShifts: string[]
  setSelectedShifts: Dispatch<SetStateAction<string[]>>
}

export type EnableSelectDays = {
  /**
   * Days in ISO format (YYYY-MM-DD)
   */
  selectedDays: string[]
  setSelectedDays: Dispatch<SetStateAction<string[]>>
}

type BookingsMode = 'compact' | 'rows'

type Week = { number: number; days: string[] }

type ScheduleContextState = {
  startDate: string
  startsAt: number
  endsAt: number
  zoom: ScheduleZoom
  headerColumnWidth: number
  columnWidth: number
  days: ScheduleDay[]
  currentDay: ScheduleDay | null
  currentWeek: Week | null
  columnCount: number
  containerWidth: number
  availableChannels: AvailableShiftChannels
  shifts: UIShift[]
  availability: UIAvailability[]
  resourcesIdMap: Map<string, Resource>
  resourcesRowKeyMap: Map<number, Resource>
  collapsedGroups: string[]
  setCollapsedGroups: Dispatch<SetStateAction<string[]>>
  enableEditing: boolean
  enableSelectShifts: boolean
  enableSelectDays: boolean
  isScrolled: boolean
  viewBookings: BookingsMode
  shiftPopover: ShiftPopover | undefined
  availabilityPopover: AvailabilityPopover | undefined
  selectedShifts: string[] | undefined
  setSelectedShifts: Dispatch<SetStateAction<string[]>> | undefined
  selectedDays: string[] | undefined
  setSelectedDays: Dispatch<SetStateAction<string[]>> | undefined
  onCreateDraftShift: ((data: DraftShiftInput) => void) | undefined
  onDeleteDraftShift: ((id: string) => void) | undefined
}

const initialState: ScheduleContextState = {
  collapsedGroups: [],
  enableEditing: false,
  enableSelectShifts: false,
  enableSelectDays: false,
  isScrolled: false,
  setCollapsedGroups: () => null,
  shiftPopover: undefined,
  availabilityPopover: undefined,
  availableChannels: {
    publishing: true,
    pools: false,
    tenders: true,
  },
  shifts: [],
  availability: [],
  resourcesIdMap: new Map(),
  resourcesRowKeyMap: new Map(),
  startDate: '',
  viewBookings: 'rows',
  startsAt: 0,
  endsAt: 0,
  zoom: 'week',
  headerColumnWidth: 0,
  columnWidth: 0,
  days: [],
  currentDay: null,
  currentWeek: null,
  columnCount: 1,
  containerWidth: 0,
  selectedShifts: undefined,
  setSelectedShifts: undefined,
  selectedDays: undefined,
  setSelectedDays: undefined,
  onCreateDraftShift: () => undefined,
  onDeleteDraftShift: () => undefined,
}

export const ScheduleContext =
  React.createContext<ScheduleContextState>(initialState)

const ScheduleProvider = (props: {
  children: React.ReactNode
  startsAt: number
  endsAt: number
  zoom: ScheduleZoom
  headerColumnWidth: number
  days: ScheduleDay[]
  shifts: UIShift[]
  availableChannels: AvailableShiftChannels | Falsy
  availability: UIAvailability[] | undefined
  resources: UICandidate[] | undefined
  enableEditing: EnableEditing | Falsy
  enableSelectShifts: EnableSelectShifts | Falsy
  enableSelectDays: EnableSelectDays | Falsy
  compactBookings: boolean | undefined
  shiftPopover: ShiftPopover | undefined
  availabilityPopover: AvailabilityPopover | undefined
}) => {
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([])

  const draftShifts = props.enableEditing
    ? props.enableEditing.draftShifts.map(convertUIDraftShiftToShift)
    : EMPTY_FROZEN_ARRAY

  const shifts = [...props.shifts, ...draftShifts]
  const availability = props.availability ?? []

  const { resourcesIdMap, resourcesRowKeyMap } = useMemo(() => {
    if (!props.resources)
      return {
        resourcesIdMap: new Map<string, Resource>(),
        resourcesRowKeyMap: new Map<number, Resource>(),
      }

    const toResource = (resource: UICandidate): Resource => ({
      rowKey: resource.rowKey,
      id: resource.id,
      fullName: resource.fullName,
      profession: resource.profession,
      homeUnitId:
        resource.contract?.homeUnitId ?? resource.candidateBooking?.unitId,
    })

    const resourcesIdMap = new Map<string, Resource>(
      props.resources.map((resource) => [resource.id, toResource(resource)]),
    )

    const resourcesRowKeyMap = new Map<number, Resource>(
      props.resources
        .filter((resource) => typeof resource.rowKey === 'number')
        .map((resource) => [resource.rowKey!, toResource(resource)]),
    )

    return { resourcesIdMap, resourcesRowKeyMap }
  }, [props.resources])

  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)
  const [currentDay, setCurrentDay] = useState<ScheduleDay>(props.days[0])

  useEffect(() => {
    setCurrentDay(props.days[0])
  }, [props.days])

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const scrollLeft = containerRef.current.scrollLeft
        if (containerWidth !== containerRef.current.clientWidth) {
          setContainerWidth(containerRef.current.clientWidth)
        }

        // Update isScrolled state
        if (scrollLeft > 0 && !isScrolled) {
          setIsScrolled(true)
        } else if (scrollLeft === 0 && isScrolled) {
          setIsScrolled(false)
        }

        const visibleColumnIndex = Math.floor(
          scrollLeft / COLUMN_WIDTH[props.zoom],
        )

        if (props.days[visibleColumnIndex]) {
          const dateISO = props.days[visibleColumnIndex].dateISO
          if (dateISO !== currentDay.dateISO) {
            const newDay = props.days.find((day) => day.dateISO === dateISO)
            if (newDay) {
              setCurrentDay(newDay)
            }
          }
        }
      }
    }

    const throttledHandleScroll = throttle(handleScroll, 100)

    const container = containerRef.current
    if (container) {
      setContainerWidth(container.clientWidth)
      container.addEventListener('scroll', throttledHandleScroll)
      return () => {
        container.removeEventListener('scroll', throttledHandleScroll)
      }
    }
  }, [props.days, props.zoom, currentDay])

  const currentWeek = currentDay
    ? ({
        number: currentDay.weekNumber,
        days: props.days
          .filter((day) => day.weekNumber === currentDay.weekNumber)
          .map(({ dateISO }) => dateISO),
      } satisfies Week)
    : null

  return (
    <Container
      ref={containerRef}
      isHorizontallyScrollable={props.days.length > 7}
    >
      <ScheduleContext.Provider
        value={{
          shiftPopover: props.shiftPopover,
          availabilityPopover: props.availabilityPopover,
          enableEditing: !!props.enableEditing,
          enableSelectShifts: !!props.enableSelectShifts,
          enableSelectDays: !!props.enableSelectDays,
          availableChannels: props.availableChannels || {
            publishing: false,
            pools: false,
            tenders: false,
          },
          isScrolled,
          startsAt: props.startsAt,
          endsAt: props.endsAt,
          zoom: props.zoom,
          headerColumnWidth: props.headerColumnWidth,
          columnWidth: COLUMN_WIDTH[props.zoom],
          startDate: props.days[0].dateISO,
          days: props.days,
          currentDay,
          currentWeek,
          // Title column + 7 days for each week
          columnCount: 1 + props.days.length,
          containerWidth,
          shifts,
          availability,
          resourcesIdMap,
          resourcesRowKeyMap,
          selectedShifts: isTruthy(props.enableSelectShifts)
            ? props.enableSelectShifts.selectedShifts
            : undefined,
          setSelectedShifts: isTruthy(props.enableSelectShifts)
            ? props.enableSelectShifts.setSelectedShifts
            : undefined,
          selectedDays: isTruthy(props.enableSelectDays)
            ? props.enableSelectDays.selectedDays
            : undefined,
          setSelectedDays: isTruthy(props.enableSelectDays)
            ? props.enableSelectDays.setSelectedDays
            : undefined,
          collapsedGroups,
          setCollapsedGroups,
          viewBookings: props.compactBookings ? 'compact' : 'rows',
          onCreateDraftShift: isTruthy(props.enableEditing)
            ? props.enableEditing.onCreate
            : undefined,
          onDeleteDraftShift: isTruthy(props.enableEditing)
            ? props.enableEditing.onDelete
            : undefined,
        }}
      >
        {props.children}
      </ScheduleContext.Provider>
    </Container>
  )
}

const useSchedule = () => {
  const context = React.useContext(ScheduleContext)
  if (context === undefined) {
    throw new Error('useSchedule must be used within a ScheduleContextProvider')
  }
  return context
}

const useGroup = (id: string) => {
  const { collapsedGroups, setCollapsedGroups } = useSchedule()

  const isOpen = !collapsedGroups.includes(id)

  const toggleOpen = () => {
    setCollapsedGroups((curr) => {
      if (isOpen) return [...curr, id]
      return curr.filter((toggledId) => toggledId !== id)
    })
  }

  return { isOpen, toggleOpen }
}

const useResources = (id: string) => {
  const { resourcesIdMap } = useSchedule()

  return { resources: resourcesIdMap, resource: resourcesIdMap.get(id) }
}

const useSelectedShifts = (ids: string[]) => {
  const { selectedShifts, setSelectedShifts, enableSelectShifts } =
    useSchedule()

  const toggleSelected = () => {
    if (!enableSelectShifts) return

    setSelectedShifts?.((curr) => {
      if (ids.every((id) => curr.includes(id))) {
        return curr.filter((id) => !ids.includes(id))
      }
      return uniq([...curr, ...ids])
    })
  }

  const isSelected = ids.some((id) => selectedShifts?.includes(id))

  return {
    isSelected,
    toggleSelected,
    enableSelectShifts,
    someShiftsSelected: !!selectedShifts?.length,
    allShiftsSelected:
      ids.every((id) => selectedShifts?.includes(id)) && !!ids.length,
  }
}

const useSelectedDays = () => {
  const { selectedDays, setSelectedDays, enableSelectDays, days } =
    useSchedule()
  const lastSelectedDateRef = useRef<string | null>(null)
  const isShiftPressedRef = useRef(false)

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      isShiftPressedRef.current = true
    }
  }, [])

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      isShiftPressedRef.current = false
    }
  }, [])

  // Set up event listeners only if enableSelectDays is true
  useEffect(() => {
    if (!enableSelectDays) return

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [enableSelectDays, handleKeyDown, handleKeyUp])

  const getDaysBetween = useCallback(
    (startDate: string, endDate: string): string[] => {
      // Find indices of both dates
      const startIndex = days.findIndex((day) => day.dateISO === startDate)
      const endIndex = days.findIndex((day) => day.dateISO === endDate)

      if (startIndex === -1 || endIndex === -1) return []

      // Get the correct range regardless of selection direction
      const [start, end] =
        startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex]

      // Include both start and end dates in the selection
      const selectedDays = days
        .slice(start, end + 1)
        .filter((day) => day.withinRange)
        .map((day) => day.dateISO)

      return selectedDays
    },
    [days],
  )

  const toggleSelected = useCallback(
    (day: string) => {
      if (!enableSelectDays) return

      const isShiftPressed = isShiftPressedRef.current
      const lastSelectedDate = lastSelectedDateRef.current

      setSelectedDays?.((curr) => {
        if (isShiftPressed && lastSelectedDate) {
          const daysBetween = getDaysBetween(lastSelectedDate, day)
          const isSelecting = !curr.includes(day)

          if (isSelecting) {
            // Add all days between last selected and current
            const newDays = new Set([...curr, ...daysBetween])
            return Array.from(newDays)
          } else {
            // Remove all days between last selected and current
            return curr.filter((d) => !daysBetween.includes(d))
          }
        } else {
          // Normal single day toggle
          if (curr.includes(day)) {
            return curr.filter((selectedDay) => selectedDay !== day)
          }
          return [...curr, day]
        }
      })

      lastSelectedDateRef.current = day
    },
    [enableSelectDays, getDaysBetween, setSelectedDays],
  )

  const isDaySelected = useCallback(
    (day: string) => !!selectedDays?.includes(day),
    [selectedDays],
  )

  return {
    isDaySelected,
    toggleSelected,
    enableSelectDays,
  }
}

export {
  ScheduleProvider,
  useSchedule,
  useGroup,
  useResources,
  useSelectedShifts,
  useSelectedDays,
  getScheduleShiftStatus,
}
